/**
 * Iter 67cc — Admin alert-pill drill-down dialog.
 *
 * Triggered from `AdminFleetHealthChip` when the admin clicks a breach pill.
 * Pulls the vendor's full per-car P&L matrix (revenue / expenses / net YTD)
 * so the admin sees the breaching car in context with the rest of the fleet
 * and can decide whether to reach out or let it ride.
 *
 * Read-only — the vendor still owns the actual P&L; this is just a window
 * into it for ops outreach.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import Money from "@/components/Money";
import {
  AlertTriangle, TrendingUp, TrendingDown, Activity, Loader2, ExternalLink, Mail,
} from "lucide-react";

export default function AdminAlertDrillDownDialog({ alert, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !alert?.vendor_id) return undefined;
    let alive = true;
    setLoading(true);
    setError(null);
    // Re-uses the existing platform endpoint, then filters client-side to the
    // breaching vendor's cars. Avoids the need for a per-vendor admin route.
    api.get("/admin/fleet-health-summary")
      .then((r) => {
        if (!alive) return;
        const all = [...(r.data?.best || []), ...(r.data?.worst || [])];
        const seen = new Set();
        const rows = all
          .filter((c) => c.vendor_id === alert.vendor_id)
          .filter((c) => { if (seen.has(c.id)) return false; seen.add(c.id); return true; });
        const vendorAlerts = (r.data?.alerts || []).filter((a) => a.vendor_id === alert.vendor_id);
        setData({ year: r.data?.year, cars: rows, alerts: vendorAlerts });
      })
      .catch((e) => { if (alive) setError(e); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, alert?.vendor_id]);

  const summary = useMemo(() => {
    if (!data?.cars) return null;
    const rev = data.cars.reduce((s, c) => s + (c.revenue_eur || 0), 0);
    const exp = data.cars.reduce((s, c) => s + (c.expenses_eur || 0), 0);
    return { revenue: rev, expenses: exp, net: rev - exp };
  }, [data]);

  if (!alert) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="admin-alert-drill-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            {alert.vendor_name}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2 flex-wrap text-xs">
            <span>{alert.car_label}</span>
            <Badge className="bg-amber-600 text-[10px]">{alert.reasons?.[0]}</Badge>
            {alert.reasons?.slice(1).map((r, i) => (
              <Badge key={i} variant="outline" className="text-[10px] bg-amber-50 text-amber-900 border-amber-200">{r}</Badge>
            ))}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-10 text-center text-neutral-500"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading vendor P&amp;L…</div>
        )}

        {error && !loading && (
          <div className="py-6 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-4">Could not load vendor data.</div>
        )}

        {!loading && data && (
          <>
            {/* Summary strip */}
            {summary && (
              <div className="grid grid-cols-3 gap-3" data-testid="admin-alert-drill-summary">
                <div className="rounded-xl bg-emerald-50 ring-1 ring-emerald-200 p-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" /> Revenue YTD
                  </div>
                  <div className="font-bold text-emerald-900 mt-1"><Money amount={summary.revenue} /></div>
                </div>
                <div className="rounded-xl bg-rose-50 ring-1 ring-rose-200 p-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-rose-700 flex items-center gap-1">
                    <TrendingDown className="h-3 w-3" /> Expenses YTD
                  </div>
                  <div className="font-bold text-rose-900 mt-1"><Money amount={summary.expenses} /></div>
                </div>
                <div className={`rounded-xl ring-1 p-3 ${summary.net >= 0 ? "bg-emerald-50 ring-emerald-200" : "bg-rose-50 ring-rose-200"}`}>
                  <div className={`text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 ${summary.net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    <Activity className="h-3 w-3" /> Net YTD
                  </div>
                  <div className={`font-bold mt-1 ${summary.net >= 0 ? "text-emerald-900" : "text-rose-900"}`}><Money amount={summary.net} /></div>
                </div>
              </div>
            )}

            {/* Per-car matrix */}
            <div className="rounded-xl border border-border overflow-hidden">
              <div className="px-4 py-2.5 border-b border-border bg-neutral-50 font-semibold text-sm">
                Fleet breakdown · {data.year}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-alert-drill-table">
                  <thead className="bg-neutral-50 border-b border-border text-[10px] uppercase tracking-wider text-neutral-600">
                    <tr>
                      <th className="text-left px-3 py-2">Vehicle</th>
                      <th className="text-right px-3 py-2">Revenue</th>
                      <th className="text-right px-3 py-2">Expenses</th>
                      <th className="text-right px-3 py-2">Net</th>
                      <th className="px-2 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.cars.length === 0 && (
                      <tr><td colSpan={5} className="px-3 py-6 text-center text-neutral-500">No active cars in the top/worst-5 strip for this vendor.</td></tr>
                    )}
                    {data.cars.map((c) => {
                      const breach = data.alerts.find((a) => a.car_id === c.id);
                      const isBreach = !!breach;
                      const isFocus = c.id === alert.car_id;
                      return (
                        <tr key={c.id} className={`border-b border-border last:border-0 ${isFocus ? "bg-amber-50/50" : ""}`} data-testid={`admin-alert-drill-row-${c.id}`}>
                          <td className="px-3 py-2.5">
                            <div className="font-medium text-xs">{c.label}{isFocus && <Badge className="ml-1 bg-amber-600 text-[9px]">Focus</Badge>}</div>
                            {c.plate && <div className="text-[10px] text-neutral-500">{c.plate}</div>}
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-xs">€{(c.revenue_eur || 0).toFixed(0)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-xs">€{(c.expenses_eur || 0).toFixed(0)}</td>
                          <td className={`px-3 py-2.5 text-right tabular-nums font-bold text-xs ${(c.net_eur || 0) >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                            €{(c.net_eur || 0).toFixed(0)}
                          </td>
                          <td className="px-2 py-2.5 text-right">
                            {isBreach && (
                              <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[9px]" title={breach.reasons.join(" · ")}>
                                <AlertTriangle className="h-2.5 w-2.5 mr-0.5" /> Alert
                              </Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
              <Button asChild variant="outline" size="sm" className="rounded-full">
                <a
                  href={`mailto:?subject=${encodeURIComponent("RentalCar.al — your fleet expenses")}&body=${encodeURIComponent("Hi " + alert.vendor_name + ",\n\nWe noticed your car '" + alert.car_label + "' has crossed your expense threshold (" + (alert.reasons || []).join(" · ") + "). Want to jump on a quick call?\n\n")}`}
                  data-testid="admin-alert-drill-email"
                >
                  <Mail className="h-3.5 w-3.5 mr-1" /> Email vendor
                </a>
              </Button>
              <Button asChild variant="default" size="sm" className="rounded-full bg-indigo-600 hover:bg-indigo-700">
                <a href={`/dashboard/admin#vendors`} data-testid="admin-alert-drill-open">
                  <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open Vendors tab
                </a>
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
